import { memo, useMemo } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Avatar } from '@components/avatar';
import { IconButton } from '@components/icon-button';
import { IconMessagesSmall } from '@components/icons/messages-small';
import { IconNotesSmall } from '@components/icons/notes-small';
import { IconPerson } from '@components/icons/person';
import { IconRatingGood } from '@components/icons/rating-good';
import { IconRatingMedium } from '@components/icons/rating-medium';
import { Stack } from '@components/stack';
import { AvailableColumn } from '@components/table/types';
import { theme } from '@styles/theme';

import { applicantStatusTranslationMap, useTranslate } from 'locale';

import { ApplicationLabels } from 'components/ApplicationLabels';
import {
  applicationDetailUrl,
  applicationMessagesUrl,
  applicationNotesUrl,
  applicationRatingsUrl,
  jobPipelineUrl,
} from 'features/routing';
import { getIconForRating } from 'helpers/reviews';
import type { ISearchApplication } from 'types/applications';

export const useApplicantsColumns = () => {
  const { t } = useTranslate();

  return useMemo<Array<AvailableColumn<ISearchApplication>>>(
    () => [
      {
        id: 'name',
        renderHeaderIcon: () => <IconPerson size={16} />,
        title: t('job.applicants.label.name'),
        type: 'text',
        Cell: ({ row }) => <RenderApplicant application={row} />,
      },
      {
        id: 'email',
        title: t('job.applicants.label.email'),
        type: 'email',
        Cell: ({ row }) => (
          <ActionText.Link
            font="regular14"
            color="textPrimary"
            to={applicationMessagesUrl(row.id, row.jobId)}
          >
            {row.email}
          </ActionText.Link>
        ),
      },
      {
        id: 'date',
        title: t('job.applicants.label.applied'),
        type: 'date',
      },
      {
        id: 'jobTitle',
        title: t('job.applicants.label.job'),
        type: 'text',
        Cell: ({ row }) => (
          <ActionText.Link font="regular14" color="textPrimary" to={jobPipelineUrl(row.jobId)}>
            {row.jobTitle}
          </ActionText.Link>
        ),
      },
      {
        id: 'jobStageName',
        title: t('job.applicants.label.stage'),
        type: 'text',
        defaultHidden: true,
      },
      {
        id: 'status',
        title: t('job.applicants.label.status'),
        type: 'text',
        Cell: ({ row }) => (
          <ApplicationStatus $status={row.status}>
            <IconRatingMedium /> {t(applicantStatusTranslationMap[row.status])}
          </ApplicationStatus>
        ),
      },
      {
        id: 'numReviews',
        title: t('job.applicants.label.reviews'),
        type: 'number',
        renderHeaderIcon: () => <IconRatingGood size={16} />,
        defaultHidden: true,
        Cell: ({ row }) => (
          <ActionText.Link
            font="regular14"
            color="textPrimary"
            to={applicationRatingsUrl(row.id, row.jobId)}
          >
            {row.numReviews > 0 ? row.numReviews : '-'}
          </ActionText.Link>
        ),
      },
      {
        id: 'averageReviews',
        title: t('job.applicants.label.avgrating'),
        type: 'single_select',
        sortText: {
          asc: t('applicants_columns.review.sort.asc'),
          desc: t('applicants_columns.review.sort.desc'),
        },
        renderHeaderIcon: () => <IconRatingMedium size={16} />,
        defaultHidden: true,
        Cell: ({ row }) => {
          return (
            <IconButton
              size="lg"
              icon={getIconForRating(row.averageReviews, true)}
              variant="ghost-purple"
              href={applicationRatingsUrl(row.id, row.jobId)}
            />
          );
        },
      },
      {
        id: 'numMessages',
        title: t('job.applicants.label.messages'),
        type: 'number',
        renderHeaderIcon: () => <IconMessagesSmall />,
        defaultHidden: true,
        Cell: ({ row }) => (
          <ActionText.Link
            font="regular14"
            color="textPrimary"
            to={applicationMessagesUrl(row.id, row.jobId)}
          >
            {row.numMessages > 0 ? row.numMessages : '-'}
          </ActionText.Link>
        ),
      },
      {
        id: 'numNotes',
        title: t('job.applicants.label.notes'),
        type: 'number',
        renderHeaderIcon: () => <IconNotesSmall />,
        defaultHidden: true,
        Cell: ({ row }) => {
          const notesCount = row.numNotes + (row.numAdminNotes ?? 0);
          return (
            <ActionText.Link
              font="regular14"
              color="textPrimary"
              to={applicationNotesUrl(row.id, row.jobId)}
            >
              {notesCount > 0 ? notesCount : '-'}
            </ActionText.Link>
          );
        },
      },
      {
        id: 'labels',
        title: t('job.applicants.label.labels'),
        type: 'multi_select',
        Cell: ({ row }) => <ApplicationLabels application={row} />,
      },
    ],
    [t]
  );
};

const RenderApplicant = memo<{ application: ISearchApplication }>(({ application }) => {
  return (
    <Stack space={2} orientation="horizontal" align="center">
      <Avatar
        src={application.picture}
        name={application.name}
        size="md"
        color={application.isRejected ? 'red' : 'green'}
      />
      <ActionText.Link
        font="regular14"
        color="textPrimary"
        to={applicationDetailUrl(application.id, application.jobId)}
      >
        {application.name}
      </ActionText.Link>
    </Stack>
  );
});

const getStatusColor = (status: string) => {
  switch (status) {
    case 'hired':
      return theme.color.elementGreen200;
    case 'rejected':
      return theme.color.red100;
    case 'retracted':
      return theme.color.orange100;
    default:
      return theme.color.elementBlue100;
  }
};

const ApplicationStatus = styled.span<{ $status: string }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  font: theme.font.regular14,
  color: getStatusColor(p.$status),
  textTransform: 'capitalize',
}));
